import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Calendar, Upload, Download, Trash2, FileText, Check, ChevronDown, ChevronUp, ShieldCheck, Share2 } from 'lucide-react';
import ShareEngagementModal from '../share/ShareEngagementModal';
import InviteDecisionPanel from '../invite/InviteDecisionPanel';
import { eventNameFor } from '../../../utils/eventName';
import * as SelfApi from '../selfApi';

const API = process.env.REACT_APP_API_URL || '';
const BLUE = '#49ABDB';

// The speaker area of the profile ("Referentenbereich"): what used to be the separate Speaker Hub
// page (/speaker/dashboard) below its hero, without any page chrome of its own - AttendeeProfile.js
// gives it its section heading and its place in the grid. /speaker/dashboard only redirects to
// /profile#referentenbereich now, so the links already sent in invitation and reminder emails land
// here. Open invitations are answered here too, inline (since 2026-10-20; before, they were a
// separate list in the profile's right column).

// Cards: the profile's own history card (AttendeeProfile.js RegCard, RoleTag, StatusTag), so the
// speaker area and the history next to it read as one system. An open invitation carries the brand
// blue on its left edge - the one kind of card on the page that waits for the person.
const SpotCard = styled.div`display:flex;flex-direction:column;background:#fff;padding:.9rem;margin-bottom:.8rem;box-shadow:0 6px 16px rgba(15,23,42,.06);scroll-margin-top:1rem;${p => (p.$open ? `border-left:4px solid ${BLUE};padding-left:calc(.9rem - 4px);` : '')}${p => (p.$highlighted ? `outline:2px solid ${BLUE};outline-offset:2px;` : '')}`;
const CardTop = styled.div`display:flex;gap:1rem;align-items:center;@media(max-width:420px){gap:.75rem;}`;
const Thumb = styled.img`width:112px;height:82px;object-fit:cover;flex:none;background:#eef2f7;@media(max-width:420px){width:84px;height:62px;}`;
const ThumbFallback = styled.div`width:112px;height:82px;flex:none;background:#eef2f7;display:flex;align-items:center;justify-content:center;color:#94a3b8;@media(max-width:420px){width:84px;height:62px;}`;
const CardInfo = styled.div`min-width:0;`;
const CardName = styled.div`font-weight:700;color:#111D4E;font-size:1.02rem;`;
const CardMeta = styled.div`color:#5B6B78;font-size:.85rem;margin-top:.12rem;`;
const RoleTag = styled.span`display:inline-block;padding:.1rem .5rem;font-size:.68rem;font-weight:800;letter-spacing:.03em;text-transform:uppercase;background:#111D4E;color:#fff;margin-right:.4rem;vertical-align:.08em;`;
const StatusTag = styled.span`display:inline-block;margin-top:.35rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:${p => (p.$confirmed ? '#dcfce7' : '#e0f2fe')};color:${p => (p.$confirmed ? '#166534' : '#075985')};`;
const PastTag = styled.span`display:inline-block;margin-top:.35rem;margin-left:.4rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:#e5e7eb;color:#6b7280;`;
// The answer itself, inline - the profile's former InviteBox: a hairline under the thumbnail row.
const InviteBox = styled.div`margin-top:.75rem;padding-top:.75rem;border-top:1px solid #eef2f7;`;
// The groups of the section (waiting for an answer, confirmed): the profile's motivation label
// (small caps over the brand gradient line) with an italic line under it.
const Group = styled.div`& + &{margin-top:1.6rem;}`;
const GroupHead = styled.div`margin:0 0 .8rem;`;
const GroupLabel = styled.h3`
  display:inline-block;margin:0;font-size:.68rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#5B6B78;
  padding-bottom:6px;background-image:linear-gradient(to right,#4e4094,#14cbde);background-size:100% 2px;background-position:0 bottom;background-repeat:no-repeat;
`;
const GroupNote = styled.div`color:#5B5B5B;font-style:italic;font-size:.85rem;margin-top:.35rem;`;
// What the person just answered, shown where the spot went: a confirmation on the confirmed card, a
// thank-you where the invitation card was (a declined spot moves to the history).
const Success = styled.div`background:#d1fae5;color:#065f46;padding:.6rem .8rem;margin-top:.7rem;font-size:.86rem;line-height:1.5;`;
const Thanks = styled.div`background:#fff;border-left:4px solid #cbd5e1;padding:.75rem .9rem;margin-bottom:.8rem;color:#334155;font-size:.88rem;line-height:1.5;box-shadow:0 6px 16px rgba(15,23,42,.06);`;
const ThanksTitle = styled.div`font-weight:700;color:#111D4E;margin-bottom:.2rem;`;
const Empty = styled.p`color:#5B6B78;`;

// Sits below the role/status tags on a confirmed engagement - any confirmed
// role (speaker, moderator, panelist) can generate a share image, not just
// SPEAKER rows (those alone get the TalkPanel below).
const ShareRow = styled.div`margin-top:.5rem;`;
const ShareButton = styled.button`display:inline-flex;align-items:center;gap:.35rem;border:1px solid #cbd5e1;background:#fff;color:#334155;padding:.35rem .7rem;font-size:.78rem;font-weight:600;cursor:pointer;transition:border-color .15s,color .15s;&:hover{border-color:${BLUE};color:${BLUE};}`;

// "Your talk" panel inside a confirmed conference card
const TalkArea = styled.div`margin-top:.7rem;padding-top:.7rem;border-top:1px solid rgba(15,23,42,.08);`;
// Collapsed-by-default disclosure header: keeps the card compact and shows a
// one-line summary of the talk (or an invite to add one) until expanded.
const ToggleRow = styled.button`display:flex;width:100%;justify-content:space-between;align-items:center;gap:.5rem;border:0;background:transparent;padding:0;cursor:pointer;text-align:left;color:#94a3b8;&:hover{color:#5B6B78;}`;
const ToggleLeft = styled.span`display:flex;flex-direction:column;gap:.15rem;min-width:0;`;
const ToggleTitle = styled.span`font-size:.68rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#334155;`;
const ToggleSummary = styled.span`display:flex;gap:.5rem;align-items:center;min-width:0;font-size:.82rem;color:#111D4E;`;
const SummaryTitle = styled.span`overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600;`;
const SummarySlides = styled.span`display:inline-flex;gap:.25rem;align-items:center;flex:none;font-size:.74rem;color:#5B6B78;`;
const TalkBody = styled.div`margin-top:.5rem;`;
const FieldLabel = styled.label`display:block;font-size:.72rem;font-weight:700;color:#5B6B78;margin:.5rem 0 .25rem;`;
const TitleRow = styled.div`display:flex;gap:.4rem;align-items:stretch;`;
const TalkInput = styled.input`flex:1;min-width:0;padding:.5rem .55rem;border:1px solid #cbd5e1;font:inherit;font-size:.9rem;`;
const MiniButton = styled.button`display:inline-flex;align-items:center;gap:.3rem;border:0;padding:.5rem .7rem;background:${p=>p.$ghost?'#eef2f7':BLUE};color:${p=>p.$ghost?'#334155':'#fff'};font-weight:700;font-size:.82rem;cursor:pointer;transition:background-color .3s;&:hover:not(:disabled){background:${p=>p.$ghost?'#e2e8f0':'#0056b3'};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const SlideBtnRow = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;align-items:center;`;
const SlideFile = styled.div`display:flex;gap:.4rem;align-items:center;min-width:0;background:#f8fafc;border:1px solid #e2e8f0;padding:.35rem .55rem;font-size:.85rem;color:#111D4E;`;
// The old slide name was an <a download> to a public uploads URL; the deck is behind the bearer
// token now (/api/me/materials/{id}/file), so it became the button that runs that download.
const SlideName = styled.button`min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:none;border:0;padding:0;font:inherit;font-size:inherit;text-align:left;color:#0369a1;cursor:pointer;&:hover:not(:disabled){text-decoration:underline;}&:disabled{opacity:.5;cursor:not-allowed;}`;
const IconButton = styled.button`display:inline-flex;align-items:center;border:0;background:transparent;color:${p=>p.$danger?'#dc2626':'#0369a1'};cursor:pointer;padding:.15rem;&:hover:not(:disabled){color:${p=>p.$danger?'#b91c1c':'#0056b3'};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const TalkHint = styled.div`font-size:.7rem;color:#94a3b8;margin-top:.4rem;`;
const TalkError = styled.div`font-size:.78rem;color:#b91c1c;margin-top:.4rem;`;
// The publish-consent answer is a real decision with consequences, so it's
// rendered as two selectable cards that spell out what each answer does to
// the slides, with the saved answer shown as a chip in the header. The PUT
// fires on click, so the chip doubles as save feedback ("Saving…" → "Answer
// saved") - the speaker never has to wonder whether the click stuck.
const ConsentBlock = styled.div`margin-top:.5rem;padding:.85rem;background:#fff;border:1px solid #e2e8f0;border-left:3px solid ${BLUE};`;
const ConsentHead = styled.div`display:flex;gap:.5rem;align-items:flex-start;margin-bottom:.65rem;`;
const ConsentIcon = styled.span`display:inline-flex;flex:none;align-items:center;justify-content:center;width:1.7rem;height:1.7rem;background:#eff9fe;color:${BLUE};`;
const ConsentQuestion = styled.p`flex:1;min-width:0;margin:0;font-size:.85rem;color:#111D4E;font-weight:600;line-height:1.35;`;
const ConsentState = styled.span`flex:none;font-size:.66rem;font-weight:800;text-transform:uppercase;letter-spacing:.03em;padding:.15rem .45rem;border-radius:999px;background:${p=>p.$tone==='granted'?'#dcfce7':p.$tone==='denied'?'#fee2e2':'#eef2f7'};color:${p=>p.$tone==='granted'?'#166534':p.$tone==='denied'?'#991b1b':'#475569'};`;
const ConsentOptions = styled.div`display:flex;flex-direction:column;gap:.4rem;`;
// Selected card: blue border on the light-blue tint already used for the
// admin's selected rows, so the answer is readable at a glance without
// relying on the (small) radio dot alone.
const ConsentOption = styled.label`
  display:flex;align-items:flex-start;gap:.55rem;padding:.55rem .65rem;border:1px solid #e2e8f0;background:#f8fafc;font-weight:400;cursor:pointer;transition:border-color .15s,background-color .15s;
  &:hover{border-color:#94a3b8;}
  ${p=>p.$selected&&`border-color:${BLUE};background:#eff9fe;`}
  ${p=>p.$disabled&&'cursor:not-allowed;opacity:.7;'}
  input{appearance:none;flex:none;width:1.05rem;height:1.05rem;margin:.12rem 0 0;border:2px solid #94a3b8;border-radius:50%;background:#fff;cursor:inherit;transition:border-width .12s;
    &:checked{border-color:${BLUE};border-width:5px;}
    &:focus-visible{outline:2px solid #4e4094;outline-offset:2px;}}
`;
const ConsentOptionText = styled.span`display:flex;flex-direction:column;gap:.1rem;min-width:0;`;
const ConsentOptionTitle = styled.span`font-size:.85rem;color:#111D4E;font-weight:600;line-height:1.3;`;
const ConsentOptionNote = styled.span`font-size:.74rem;color:#5B6B78;line-height:1.3;`;
const ConsentPrivacyHint = styled.div`font-size:.72rem;color:#94a3b8;margin-top:.5rem;`;

// Per-spot talk editor: title suggestion (explicit save) + slides upload/download/remove.
// Every action returns the refreshed talk, which the parent swaps into the list.
// `defaultOpen`: a spot accepted a moment ago opens on its talk, the next thing to do.
const TalkPanel = ({ talk, onUpdated, defaultOpen }) => {
  const intl = useIntl();
  const t = (id, def) => intl.formatMessage({ id, defaultMessage: def });
  const [open, setOpen] = useState(Boolean(defaultOpen));
  // The entry title is the organizer's; the site language picks which of its two forms is shown
  // (English-first for an English visitor, as utils/eventName.js does for events).
  const entryTitle = (intl.locale || '').startsWith('en')
    ? (talk.titleEn || talk.titleDe || '') : (talk.titleDe || talk.titleEn || '');
  const [title, setTitle] = useState(talk.titleSuggestion || entryTitle || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState('');
  const fileRef = useRef(null);
  const currentTitle = talk.titleSuggestion || entryTitle || '';
  const changed = title.trim() !== currentTitle;
  const adminOwned = Boolean(talk.titleEn || talk.titleDe);

  const saveTitle = async () => {
    setSaving(true); setError('');
    try {
      const answer = await SelfApi.suggestTalkTitle(talk.entryId, title);
      onUpdated({ ...talk, titleSuggestion: answer.titleSuggestion });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { setError(err.response?.data || t('speaker.talk.saveError', 'Could not save the talk title.')); }
    finally { setSaving(false); }
  };
  const pickFile = () => fileRef.current?.click();
  const onFile = async e => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setUploading(true); setError('');
    try { onUpdated(await SelfApi.uploadDeck(talk.entryId, file)); }
    catch (err) { setError(err.response?.data || t('speaker.talk.uploadError', 'Upload failed. Please try again.')); }
    finally { setUploading(false); }
  };
  const downloadSlides = async () => {
    setDownloading(true); setError('');
    try { await SelfApi.downloadDeck(talk.deck.id, talk.deck.fileName); }
    catch (err) { setError(err.response?.data || t('speaker.talk.downloadError', 'Could not download the file. Please try again.')); }
    finally { setDownloading(false); }
  };
  const removeSlides = async () => {
    setUploading(true); setError('');
    try { onUpdated(await SelfApi.removeDeck(talk.entryId)); }
    catch (err) { setError(err.response?.data || t('speaker.talk.removeError', 'Could not remove the file.')); }
    finally { setUploading(false); }
  };
  const setConsent = async consent => {
    setConsentSaving(true); setConsentError('');
    try {
      const answer = await SelfApi.setMaterialsConsent(talk.assignmentId, consent);
      onUpdated({ ...talk, consent: answer.consent });
    } catch (err) { setConsentError(err.response?.data || t('speaker.talk.consentSaveError', 'Could not save your answer. Please try again.')); }
    finally { setConsentSaving(false); }
  };
  const privacyPolicyUrl = `${API}/uploads/privacy_policy_${intl.locale === 'de' ? 'de' : 'en'}.pdf`;

  // A talk of GET /api/me/talks is one the person is a confirmed speaker of: an entry with a material
  // mode (slides to upload, consent to give) or one that asks for no slides (mode NONE) and carries
  // only the title - the old "No presentation is required" variant. The deck is the speaker's own file
  // or the shared one.
  const needsSlides = talk.mode !== 'NONE';
  const deck = talk.deck;
  const hasContent = Boolean(entryTitle || deck);
  const consent = talk.consent || 'UNSET';
  const consentAnswered = consent !== 'UNSET';
  // Consent gates the *first* upload (the service refuses one while the question is unanswered). It
  // must not gate a replacement: a speaker whose answer was reset while a deck is already stored
  // would otherwise be locked out of swapping the file they own.
  const needsConsentFirst = !consentAnswered && !deck;

  return (
    <TalkArea>
      <ToggleRow type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <ToggleLeft>
          <ToggleTitle>
            {hasContent
              ? <FormattedMessage id="speaker.talk.header" defaultMessage="Your talk" />
              : needsSlides
                ? <FormattedMessage id="speaker.talk.add" defaultMessage="Add your talk & slides" />
                : <FormattedMessage id="speaker.talk.addTitleOnly" defaultMessage="Add your talk title" />}
          </ToggleTitle>
          {!open && hasContent && (
            <ToggleSummary>
              {entryTitle && <SummaryTitle>{entryTitle}</SummaryTitle>}
              {deck && <SummarySlides><FileText size={12} />{deck.fileName}</SummarySlides>}
            </ToggleSummary>
          )}
        </ToggleLeft>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </ToggleRow>
      {open && (
      <TalkBody>
      <FieldLabel>{adminOwned
        ? <FormattedMessage id="speaker.talk.titleAdminLabel" defaultMessage="Talk title (set by organizer)" />
        : <FormattedMessage id="speaker.talk.titleLabel" defaultMessage="Talk title" />}</FieldLabel>
      <TitleRow>
        <TalkInput value={title} onChange={e => setTitle(e.target.value)} placeholder={t('speaker.talk.titlePlaceholder', 'e.g. The Future of Valve Repair')} />
        <MiniButton onClick={saveTitle} disabled={saving || !changed}>
          {saved ? <><Check size={14} /><FormattedMessage id="speaker.talk.saved" defaultMessage="Saved" /></>
                 : adminOwned
                   ? (talk.titleSuggestion
                       ? <FormattedMessage id="speaker.talk.suggestPending" defaultMessage="Change requested" />
                       : <FormattedMessage id="speaker.talk.suggest" defaultMessage="Suggest change" />)
                   : <FormattedMessage id="speaker.talk.save" defaultMessage="Save" />}
        </MiniButton>
      </TitleRow>
      {adminOwned && (
        <TalkHint>
          {talk.titleSuggestion
            ? <FormattedMessage id="speaker.talk.pending" defaultMessage="Title change request pending review" />
            : <FormattedMessage id="speaker.talk.suggestionHint" defaultMessage="The organizer will review your suggestion." />}
        </TalkHint>
      )}

      <FieldLabel><FormattedMessage id="speaker.talk.slidesLabel" defaultMessage="Slides" /></FieldLabel>
      {needsSlides || deck ? (
        <>
          <ConsentBlock>
            <ConsentHead>
              <ConsentIcon><ShieldCheck size={16} /></ConsentIcon>
              <ConsentQuestion><FormattedMessage id="speaker.talk.consentQuestion" defaultMessage='May we publish your presentation materials (e.g. on the HEARTS website) in support of "Democratizing Science"?' /></ConsentQuestion>
              {consentSaving
                ? <ConsentState><FormattedMessage id="speaker.talk.consentSaving" defaultMessage="Saving…" /></ConsentState>
                : consentAnswered && (
                  <ConsentState $tone={consent === 'GRANTED' ? 'granted' : 'denied'}>
                    <FormattedMessage id="speaker.talk.consentSaved" defaultMessage="Answer saved" />
                  </ConsentState>
                )}
            </ConsentHead>
            <ConsentOptions>
              <ConsentOption $selected={consent === 'GRANTED'} $disabled={consentSaving}>
                <input type="radio" name={`materialsConsent-${talk.assignmentId}`} checked={consent === 'GRANTED'} disabled={consentSaving} onChange={() => setConsent('GRANTED')} />
                <ConsentOptionText>
                  <ConsentOptionTitle><FormattedMessage id="speaker.talk.consentYes" defaultMessage="Yes, you may publish my presentation materials" /></ConsentOptionTitle>
                  <ConsentOptionNote><FormattedMessage id="speaker.talk.consentYesNote" defaultMessage="Your slides may be shown publicly on the HEARTS website." /></ConsentOptionNote>
                </ConsentOptionText>
              </ConsentOption>
              <ConsentOption $selected={consent === 'DENIED'} $disabled={consentSaving}>
                <input type="radio" name={`materialsConsent-${talk.assignmentId}`} checked={consent === 'DENIED'} disabled={consentSaving} onChange={() => setConsent('DENIED')} />
                <ConsentOptionText>
                  <ConsentOptionTitle><FormattedMessage id="speaker.talk.consentNo" defaultMessage="No, please keep my presentation materials internal only" /></ConsentOptionTitle>
                  <ConsentOptionNote><FormattedMessage id="speaker.talk.consentNoNote" defaultMessage="Your slides stay visible to the organizing team only." /></ConsentOptionNote>
                </ConsentOptionText>
              </ConsentOption>
            </ConsentOptions>
            <ConsentPrivacyHint>
              <FormattedMessage id="speaker.talk.consentPrivacyHint" defaultMessage="You can change your answer at any time. See our {privacyPolicy} for details."
                values={{ privacyPolicy: <a href={privacyPolicyUrl} target="_blank" rel="noreferrer"><FormattedMessage id="footer.privacyPolicy" defaultMessage="Privacy Policy" /></a> }} />
            </ConsentPrivacyHint>
            {consentError && <TalkError>{consentError}</TalkError>}
          </ConsentBlock>
          <input ref={fileRef} type="file" accept=".pptx,.ppt" style={{ display: 'none' }} onChange={onFile} />
          {deck ? (
            <SlideBtnRow>
              <SlideFile>
                <FileText size={15} color="#5B6B78" />
                <SlideName type="button" disabled={downloading} onClick={downloadSlides} title={deck.fileName}>
                  {deck.fileName || t('speaker.talk.slidesLabel', 'Slides')}
                </SlideName>
                <IconButton disabled={downloading} onClick={downloadSlides} title={t('speaker.talk.download', 'Download')}><Download size={15} /></IconButton>
                <IconButton $danger disabled={uploading} onClick={removeSlides} title={t('speaker.talk.remove', 'Remove')}><Trash2 size={15} /></IconButton>
              </SlideFile>
              <MiniButton $ghost disabled={uploading || needsConsentFirst} onClick={pickFile}>
                <Upload size={14} />{uploading ? t('speaker.talk.uploading', 'Uploading…') : t('speaker.talk.replace', 'Replace')}
              </MiniButton>
            </SlideBtnRow>
          ) : (
            <MiniButton $ghost disabled={uploading || needsConsentFirst} onClick={pickFile}>
              <Upload size={14} />{uploading ? t('speaker.talk.uploading', 'Uploading…') : t('speaker.talk.upload', 'Upload slides')}
            </MiniButton>
          )}
          {needsConsentFirst && <TalkHint><FormattedMessage id="speaker.talk.consentRequiredHint" defaultMessage="Please choose an option above before uploading your slides." /></TalkHint>}
          <TalkHint><FormattedMessage id="speaker.talk.formats" defaultMessage="PowerPoint, Keynote, PDF or ODP · up to 100 MB" /></TalkHint>
        </>
      ) : (
        <TalkHint><FormattedMessage id="speaker.talk.slidesNotRequired" defaultMessage="No presentation is required for this engagement." /></TalkHint>
      )}
      {error && <TalkError>{error}</TalkError>}
      </TalkBody>
      )}
    </TalkArea>
  );
};

// Event thumbnail with graceful fallback (missing URL or image that fails to load).
const EventThumb = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <ThumbFallback><Calendar size={22} /></ThumbFallback>;
  return <Thumb src={`${API}${url}`} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
};

// Purely about speaking engagements (talk title, slides) - name, bio, specialty, photo, interests etc.
// are the profile's own, around this section.
//
// The one place on the profile for everything about speaking (owner, 2026-10-20). The cards are the
// person's spots (D10), handed in by the profile, which reads GET /api/me/invitations for its history
// anyway (and reads it again after an answer, through onAnswered). Two groups:
//   - "Your invitations": every spot still waiting for an answer, answered right on its card
//     (InviteDecisionPanel, that one spot - its per-spot rules, 409 handling and texts are its own);
//   - "Confirmed": every confirmed spot, with the share button, and for a SPEAKER spot the talk editor
//     for its entry (GET /api/me/talks, read here).
// Declined, withdrawn and cancelled spots need nothing more and are the profile's history rows only
// (see inSpeakerArea); a confirmed spot of a past event is both: managed here (the slides and the
// consent still matter after the talk), recorded in the history.
const statusFor = (spotState) => (spotState === 'OPEN' ? 'INVITED' : spotState);
const isPast = (dateTime) => Boolean(dateTime) && new Date(dateTime) < new Date();

// Which spots this section shows: the ones the person still answers or manages.
export const inSpeakerArea = (spot) => spot.state === 'OPEN' || spot.state === 'CONFIRMED';

// The cards of the person's spots, newest first, as the old hub listed its engagement rows (the
// spots of one invitation share its time: the later one first).
const rowsFrom = (invitations, talks) => {
  const list = [];
  (invitations || []).forEach((group) => {
    (group.spots || []).filter(inSpeakerArea).forEach((spot) => {
      const isTalk = spot.role === 'SPEAKER';
      list.push({
        key: `${group.eventId}-${spot.assignmentId}`,
        assignmentId: spot.assignmentId,
        eventId: group.eventId,
        eventName: group.event?.name,
        eventNameEN: group.event?.nameEn,
        eventDateTime: group.event?.dateTime,
        eventLocation: group.event?.location,
        eventImageUrl: group.event?.imageUrl,
        role: spot.role,
        status: statusFor(spot.state),
        // The old card meta: the session with its time, and a talk's own time ("Your talk: ...")
        // when it has one of its own; a chair's or panelist's spot is the session itself.
        sessionTitle: isTalk ? spot.blockTitle : (spot.entryTitle || spot.blockTitle),
        sessionTime: isTalk ? (spot.blockTitle ? spot.blockTime : null) : spot.time,
        talkTime: isTalk && spot.time && spot.time !== spot.blockTime ? spot.time : null,
        // The talk row of GET /api/me/talks for this spot: the entry the person's materials (or, for
        // an entry that asks for no slides, the title) belong to. The service answers one for every
        // confirmed SPEAKER assignment, so a confirmed speaker spot without slides still has one.
        talk: Array.isArray(talks) ? (talks.find((row) => row.assignmentId === spot.assignmentId) || null) : null,
        sortAt: String(spot.askedAt || '').slice(0, 19),
        sortTie: spot.invitationId || 0,
      });
    });
  });
  return list.sort((a, b) => b.sortAt.localeCompare(a.sortAt) || b.sortTie - a.sortTie);
};

// Whether the profile shows this section at all: only for a person who was asked to a spot (any
// state) - a plain attendee never sees an empty speaker area.
export const hasSpeakerSpots = (invitations) => (invitations || []).some((group) => (group.spots || []).length > 0);

// `onAnswered`: the profile reads its lists again after an answer. `highlightEventId`: the event of
// the ?invite= link the profile was opened with - its cards are outlined. `registerCard(eventId, el)`:
// hands the profile each card, so it can scroll to the event's first one (the invitation, while open).
const SpeakerArea = ({ invitations, identity, onAnswered, highlightEventId, registerCard }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  // null while loading, { error: true } when the read failed
  const [talks, setTalks] = useState(null);
  const [shareEngagement, setShareEngagement] = useState(null);
  // What the person answered here, per assignment: {action: 'accepted' | 'declined', suggested} -
  // shown where the spot went once the profile has read the invitations again.
  const [answered, setAnswered] = useState({});

  // Read again whenever the invitations change: a spot just accepted is a confirmed SPEAKER
  // assignment now, with a talk row of its own.
  useEffect(() => {
    let cancelled = false;
    SelfApi.fetchTalks()
      .then((view) => { if (!cancelled) setTalks((view && view.talks) || []); })
      .catch(() => { if (!cancelled) setTalks({ error: true }); });
    return () => { cancelled = true; };
  }, [invitations]);

  const updateTalk = (talk) => {
    setTalks((current) => (Array.isArray(current) ? current : []).map((row) => (
      row.assignmentId === talk.assignmentId ? talk : row
    )));
  };

  const onResolved = (answer) => {
    if (answer?.assignmentId) {
      setAnswered((current) => ({ ...current, [answer.assignmentId]: { action: answer.action, suggested: answer.suggested } }));
    }
    onAnswered?.();
  };

  const statusLabel = s => t(`speaker.engagement.${s}`, s);
  const roleLabel = r => t(`profile.history.role.${r}`, r);
  const formatDate = v => v ? new Date(v).toLocaleString(intl.locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const sessionLabel = item => [
    item.sessionTitle,
    item.sessionTime,
    item.talkTime && t('speaker.dashboard.talkTime', 'Your talk: {time}', { time: item.talkTime }),
  ].filter(Boolean).join(' · ');

  const rows = rowsFrom(invitations, talks);
  const openRows = rows.filter((row) => row.status === 'INVITED');
  // upcoming events first, then the past ones; within each, newest first as above
  const confirmedRows = rows.filter((row) => row.status === 'CONFIRMED')
    .sort((a, b) => Number(isPast(a.eventDateTime)) - Number(isPast(b.eventDateTime)));
  // Spots declined here a moment ago: their card is gone (the spot is a history row now), the
  // thank-you stays where it was.
  const stillOpen = new Set(openRows.map((row) => row.assignmentId));
  const declinedHere = Object.entries(answered)
    .filter(([id, answer]) => answer.action === 'declined' && !stillOpen.has(Number(id)));
  const highlighted = (item) => Boolean(highlightEventId) && String(item.eventId) === String(highlightEventId);

  const cardHead = (item, meta) => (
    <CardTop>
      <EventThumb url={item.eventImageUrl} />
      <CardInfo>
        <CardName>
          <RoleTag>{roleLabel(item.role)}</RoleTag>
          {eventNameFor(intl.locale, item.eventName, item.eventNameEN)}
        </CardName>
        <CardMeta>{meta}</CardMeta>
        <StatusTag $confirmed={item.status === 'CONFIRMED'}>{statusLabel(item.status)}</StatusTag>
        {isPast(item.eventDateTime) && <PastTag>{t('profile.history.past', 'Past')}</PastTag>}
      </CardInfo>
    </CardTop>
  );
  const cardRef = (item) => (el) => { if (el) registerCard?.(item.eventId, el); };

  return (
    <>
      {declinedHere.map(([id, answer]) => (
        <Thanks key={`thanks-${id}`} role="status">
          <ThanksTitle>{t('speaker.respond.declined.title', 'Thank you for letting us know')}</ThanksTitle>
          {answer.suggested
            ? t('speaker.respond.declined.withSuggestion', 'We have passed your answer and your suggestion on to the organizers. We are sorry you can\'t join us this time and hope to welcome you at a future event.')
            : t('speaker.respond.declined.body', 'We have passed your answer on to the organizers. We are sorry you can\'t join us this time and hope to welcome you at a future event.')}
        </Thanks>
      ))}

      {openRows.length > 0 && (
        <Group data-testid="speaker-area-open">
          <GroupHead>
            <GroupLabel>{t('profile.invitations.title', 'Your invitations')}</GroupLabel>
            <GroupNote>{openRows.length > 1
              ? t('speaker.respond.eachSpot', 'Answer each spot on its own – accepting one does not answer the others.')
              : t('profile.invitations.subtitle', 'Answer each spot you were asked about')}</GroupNote>
          </GroupHead>
          {openRows.map((item) => (
            <SpotCard key={item.key} $open $highlighted={highlighted(item)} ref={cardRef(item)}>
              {/* The panel names the session, the talk and its time itself. */}
              {cardHead(item, [formatDate(item.eventDateTime), item.eventLocation].filter(Boolean).join(' · '))}
              <InviteBox>
                <InviteDecisionPanel eventId={item.eventId} assignmentId={item.assignmentId} compact onResolved={onResolved} />
              </InviteBox>
            </SpotCard>
          ))}
        </Group>
      )}

      {confirmedRows.length > 0 && (
        <Group data-testid="speaker-area-confirmed">
          <GroupHead>
            <GroupLabel>{t('profile.speakerArea.confirmed', 'Your confirmed engagements')}</GroupLabel>
          </GroupHead>
          {talks?.error && <Empty>{t('speaker.dashboard.loadError', 'Your speaking engagements could not be loaded.')}</Empty>}
          {confirmedRows.map((item) => (
            <SpotCard key={item.key} $highlighted={highlighted(item)} ref={cardRef(item)}>
              {/* Session detail and the venue are joined, not either/or: a session-titled row used to
                  silently drop the venue. A session-less row filters down to just the venue. */}
              {cardHead(item, [formatDate(item.eventDateTime), sessionLabel(item), item.eventLocation].filter(Boolean).join(' · '))}
              {answered[item.assignmentId]?.action === 'accepted' && (
                <Success role="status">{t('speaker.respond.accepted.body', "You're confirmed - we look forward to welcoming you.")}</Success>
              )}
              {/* Any confirmed role can share the engagement, not just SPEAKER - a moderator/panelist
                  still has a name/photo/slot worth posting, even with no talk of their own. */}
              {identity && (
                <ShareRow>
                  <ShareButton type="button" onClick={() => setShareEngagement(item)}>
                    <Share2 size={13} />{t('speaker.share.button', 'Share this engagement')}
                  </ShareButton>
                </ShareRow>
              )}
              {/* Only SPEAKER engagements have a talk/slides to manage - a moderator/panelist row has
                  nothing else to add here. */}
              {item.role === 'SPEAKER' && item.talk && (
                <TalkPanel talk={item.talk} onUpdated={updateTalk} defaultOpen={answered[item.assignmentId]?.action === 'accepted'} />
              )}
            </SpotCard>
          ))}
        </Group>
      )}

      {openRows.length === 0 && confirmedRows.length === 0 && (
        <Empty>
          <FormattedMessage id="speaker.dashboard.none" defaultMessage="You have no upcoming speaking engagements right now." />
          {' '}{t('profile.speakerArea.answersInHistory', 'Your earlier answers are listed under "HEARTS Conferences".')}
        </Empty>
      )}

      {shareEngagement && identity && (
        <ShareEngagementModal
          engagement={{
            ...shareEngagement,
            id: shareEngagement.key,
            talkTitle: shareEngagement.talk ? (shareEngagement.talk.titleEn || shareEngagement.talk.titleDe) : undefined,
            talkTitleSuggestion: shareEngagement.talk ? shareEngagement.talk.titleSuggestion : undefined,
            // the slot the card prints: the talk's own time, else the session's
            sessionStartTime: (shareEngagement.sessionTime || '').split('–')[0] || undefined,
            sessionEndTime: (shareEngagement.sessionTime || '').split('–')[1] || undefined,
            talkStartTime: (shareEngagement.talkTime || '').split('–')[0] || undefined,
            talkEndTime: (shareEngagement.talkTime || '').split('–')[1] || undefined,
          }}
          identity={identity}
          onClose={() => setShareEngagement(null)}
        />
      )}
    </>
  );
};

export default SpeakerArea;
